//
import {
  createRouter,
  createWebHashHistory,
  createWebHistory,
  type RouterOptions,
} from 'vue-router';
import { CapacitorFacade } from '@/plugins/capacitorPlugin';
import { afterEachNavigationGuard } from './navigationGuards/globalAfterEachHook';
import { globalBeforeEachGuard } from './navigationGuards/globalBeforeEachGuard';
import { reloadToDashboardGuard } from './navigationGuards/reloadToDashboardGuard';
import routes from './routes';
import type { URLOpenListenerEvent } from '@capacitor/app';
import { RouteNames } from './routeNames';
import DynatracePlugin from '@/plugins/dynatracePlugin';

const isMobileApp = CapacitorFacade.isNativePlatform();

const routerOptions: RouterOptions = {
  history: isMobileApp
    ? createWebHashHistory()
    : createWebHistory(window.appConfig.auth.redirectUriBase),
  routes,
  scrollBehavior(_to, _from, savedPosition) {
    return savedPosition ?? { top: 0 };
  },
};

const router = createRouter(routerOptions);

router.beforeEach(reloadToDashboardGuard);
router.beforeEach(globalBeforeEachGuard);
router.afterEach(afterEachNavigationGuard);

// * Listen for url open events for the app and handle deep link
const deeplinkHandler = async (event: URLOpenListenerEvent) => {
  // see https://capacitorjs.com/docs/apis/app#urlopenlistenerevent
  const { url } = event; // The URL the app was opened with.

  // get the url path
  const slug = url ? url.split('com.primerica.myprimerica').pop() : '';
  const searchParams = new URL(url).searchParams;
  const query = Object.fromEntries(searchParams);

  // We only push to the route if there is a slug present
  if (slug) {
    await router.push({
      path: slug,
      query,
    });
  } else {
    await router.push({ name: RouteNames.DASHBOARD });
  }
};

CapacitorFacade.addListenerOnAppUrlOpen(deeplinkHandler).catch((e) => {
  DynatracePlugin.reportCustomError(
    'PC2_MOBILE_ERR',
    'Error Adding Listener OnAppUrlOpen!',
    JSON.stringify(e)
  );
});

export default router;
